import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Guerriero L2-5 (manuale V1, rr. 3490-3575).
 *
 * 16 mosse totali. Esercitano per la prima volta in DWR il pattern
 * `prerequisites` con `anyOf` (FI-19 risolto in questa PR):
 *  - *Combattente Versatile*: anyOf [Parata, Tecniche Segrete di Combattimento]
 *  - *Cimelio*: anyOf [Arma Prescelta, **warlock.vincolo-della-lama** ← cross-class]
 *  - *Arma Migliorata*: anyOf [Arma Prescelta, warlock.vincolo-della-lama]
 *
 * I cross-class refs (alla mossa del Warlock *Vincolo della Lama*) sono
 * leciti ma il Warlock non è ancora trascritto; il test di integrità
 * referenziale accetta cross-class non verificate.
 *
 * *Compagno d'Armi* e *Combattente Multiclasse* sono mosse multiclasse:
 * la regola "scegli una mossa di X classi" vive nella description (FI-17).
 */

const combattenteVersatile: Move = {
  id: 'guerriero.combattente-versatile',
  name: 'Combattente Versatile',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Al level-up (livello 2-5)',
  description:
    'Se hai già la mossa *Tecniche Segrete di Combattimento*, prendi *Parata*, e viceversa.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede la mossa iniziale rimanente del gruppo del Guerriero
  // (Parata / Tecniche Segrete di Combattimento — pool=1 residuo).
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const ilColtelloDallaPartaDelManico: Move = {
  id: 'guerriero.il-coltello-dalla-parte-del-manico',
  name: 'Il Coltello dalla Parte del Manico',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'In ogni momento (estende *Parata*)',
  description:
    "Aggiungi le seguenti opzioni a *Parata*:\n\n- disarmi il tuo avversario, facendo volare la sua arma dall'altra parte della stanza (se è possibile);\n- crei un'opportunità per un compagno: indica un alleato (non te stesso) che avrà +1 al prossimo tiro contro il tuo avversario.",
  tags: [],
  prerequisites: ['guerriero.parata'],
  replaces: [],
};

const tecnicheAvanzateDiCombattimento: Move = {
  id: 'guerriero.tecniche-avanzate-di-combattimento',
  name: 'Tecniche Avanzate di Combattimento',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'In ogni momento (estende *Tecniche Segrete di Combattimento*)',
  description:
    "Aggiungi le opzioni seguenti a *Tecniche Segrete di Combattimento*:\n\n- *fare il morto*: fai finta di essere spacciato e l'avversario non ti considera più una minaccia (per ora);\n- *tranello*: il GM ti svela una capacità o un punto debole del nemico che ancora non conosci (se c'è).",
  tags: [],
  prerequisites: ['guerriero.tecniche-segrete-di-combattimento'],
  replaces: [],
};

const cimelio: Move = {
  id: 'guerriero.cimelio',
  name: 'Cimelio',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger:
    'Quando interroghi gli spiriti che risiedono nella Tua Arma riguardo qualcosa che essi possono conoscere (a discrezione del GM)',
  description:
    "Nella Tua Arma risiedono uno o più spiriti. Puoi comprenderli ed essere compreso da essi. Usa *declamare conoscenze* tirando+**CAR** invece che +**INT**. Se la Tua Arma ha il miglioramento «*antica*», ottieni +1 a questo tiro.\n\nAlla presa della mossa, descrivi gli spiriti dell'arma (o lascia decidere al GM lasciando il campo vuoto).",
  tags: [],
  // FI-19: alternativa cross-class. Vincolo della Lama è del Warlock,
  // accessibile via mossa multiclasse del Guerriero.
  prerequisites: [{ anyOf: ['guerriero.arma-prescelta', 'warlock.vincolo-della-lama'] }],
  replaces: [],
  // FI-18: setup-levelup narrative field. La descrizione runtime delle
  // manifestazioni degli spiriti a ogni invocazione resta gestita in
  // modalità "giocabile" (fiction live al tavolo), non in `narrativeFields`.
  narrativeFields: [
    {
      id: 'descrizione-spiriti',
      label: 'Descrivi gli spiriti della Tua Arma',
      placeholder: 'es. Un antico re caduto in battaglia, una druidessa che protegge la lama…',
      required: false, // il GM può decidere, quindi opzionale
      multiline: true,
    },
  ],
};

const settimoSenso: Move = {
  id: 'guerriero.settimo-senso',
  name: 'Settimo Senso',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger:
    'Quando chiudi gli occhi e *discerni la realtà* con i sensi degli spiriti che dimorano nella Tua Arma',
  description:
    'Tira+**CAR** anziché +**SAG**. Se la Tua Arma ha il miglioramento «*antica*», prendi +1 al tiro.',
  tags: [],
  prerequisites: ['guerriero.cimelio'],
  replaces: [],
};

const armaMigliorata: Move = {
  id: 'guerriero.arma-migliorata',
  name: 'Arma Migliorata',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Al level-up (livello 2-5)',
  description: 'Scegli un miglioramento aggiuntivo per la Tua Arma.',
  tags: [],
  prerequisites: [{ anyOf: ['guerriero.arma-prescelta', 'warlock.vincolo-della-lama'] }],
  replaces: [],
};

const lOdoreDelSangue: Move = {
  id: 'guerriero.l-odore-del-sangue',
  name: "L'Odore del Sangue",
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Quando fai *taglia e spacca*',
  description: 'Se infliggi danno, infliggi +1d4 danni.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const pelleDiFerro: Move = {
  id: 'guerriero.pelle-di-ferro',
  name: 'Pelle di Ferro',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'In ogni momento (passiva)',
  description: 'Ottieni +1 Armatura.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const maestriaInArmatura: Move = {
  id: 'guerriero.maestria-in-armatura',
  name: 'Maestria in Armatura',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger:
    "Quando stai per subire danno e lasci che la tua armatura assorba il grosso dell'impatto",
  description:
    "Il danno viene annullato, ma devi ridurre di 1 il valore di Armatura della tua corazza o del tuo scudo (a tua scelta). Il punteggio viene ridotto ogni volta che fai questa scelta. Se l'oggetto rimane con 0 Armatura, è distrutto.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const vedereRosso: Move = {
  id: 'guerriero.vedere-rosso',
  name: 'Vedere Rosso',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Quando *discerni la realtà* durante il combattimento',
  description: 'Prendi +1.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const occhioPerLeArmi: Move = {
  id: 'guerriero.occhio-per-le-armi',
  name: 'Occhio per le Armi',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger:
    'Quando osservi per la prima volta le armi del nemico con attenzione (anche quelle naturali)',
  description:
    "Puoi chiedere al GM quanti danni infliggono e che etichette hanno. Inoltre, hai +1 al prossimo tiro che effettui contro quell'avversario.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const interrogatorio: Move = {
  id: 'guerriero.interrogatorio',
  name: 'Interrogatorio',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Quando *parlamenti* con qualcuno utilizzando come leva la sua incolumità fisica',
  description: 'Puoi tirare+**FOR** invece che +**CAR**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const veterano: Move = {
  id: 'guerriero.veterano',
  name: 'Veterano',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger:
    "Quando incontri qualcuno che è stato un tuo compagno d'armi o che ha sentito parlare delle tue gesta sul campo di battaglia (a tua discrezione)",
  description:
    'Racconta un aneddoto di cui è a conoscenza e tira+**CAR**. Con 10+, ti deve un favore o ha grande stima di te, ed è disposto ad aiutarti senza nulla pretendere. Con 7-9, ti aiuterà comunque, ma vuole qualcosa in cambio. Con un 6-, in realtà ti odia, anche se non lo da a vedere: preparati a ricevere qualche brutto scherzo non appena ti distrai un attimo.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Ti deve un favore o ha grande stima di te, ti aiuta senza pretendere.' },
    on7to9: { text: 'Ti aiuta, ma vuole qualcosa in cambio.' },
    on6minus: { text: 'In realtà ti odia, preparati a brutti scherzi.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilMioTesoro: Move = {
  id: 'guerriero.il-mio-tesoro',
  name: 'Il Mio Tesssoro!',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Quando ti *accampi* e passi del tempo a prenderti cura del tuo equipaggiamento',
  description:
    "Puoi scegliere se ripristinare 1 punto di Armatura perduto dal tuo scudo o dalla tua corazza, o se aggiungere a un'arma qualsiasi un miglioramento dalla lista di *Arma Prescelta*. In quest'ultimo caso, il miglioramento dura fino alla prossima volta che ti *accampi* o fino al termine del primo combattimento in cui l'arma viene usata.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const compagnoDArmi: Move = {
  id: 'guerriero.compagno-d-armi',
  name: "Compagno d'Armi",
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse ristretta',
  description:
    'Mossa *multiclasse*. Scegli una mossa non multiclasse del **Paladino** o del **Ranger**.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto fisso a Paladino + Ranger.
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['paladino', 'ranger'] },
  },
};

const combattenteMulticlasse: Move = {
  id: 'guerriero.combattente-multiclasse',
  name: 'Combattente Multiclasse',
  type: 'advanced_2_5',
  classId: 'guerriero',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse generica',
  description:
    "Mossa *multiclasse*. Scegli una mossa non multiclasse da un'altra classe. Ai fini della scelta, considera il tuo livello come fosse inferiore di uno.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1.
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: { delta: -1 },
  },
};

export const guerrieroAdvanced2to5Moves: readonly Move[] = [
  combattenteVersatile,
  ilColtelloDallaPartaDelManico,
  tecnicheAvanzateDiCombattimento,
  cimelio,
  settimoSenso,
  armaMigliorata,
  lOdoreDelSangue,
  pelleDiFerro,
  maestriaInArmatura,
  vedereRosso,
  occhioPerLeArmi,
  interrogatorio,
  veterano,
  ilMioTesoro,
  compagnoDArmi,
  combattenteMulticlasse,
] as const;
